import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../context/AppContext';
import { authorizationApi } from '../../../services/authorizationApiService';
import RoleAssignmentModal from './components/RoleAssignmentModal';
import Button from '../../common/Button/Button';
import SearchBar from '../../common/SearchBar/SearchBar';
import Table from '../../common/Table/Table';

export default function UsersTab({ onRefresh }) {
  const { showToast } = useAppContext();
  const [cognitoUsers, setCognitoUsers] = useState([]);
  const [dbUsers, setDbUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load database users and roles
      const [dbUsersData, rolesData] = await Promise.all([
        authorizationApi.getUsers(),
        authorizationApi.getRoles()
      ]);

      // Extract arrays from response objects
      setDbUsers(dbUsersData?.users || []);
      setRoles(rolesData?.roles || []);

      // Load Cognito users using authorization-specific endpoint
      const cognitoData = await authorizationApi.getCognitoUsers();
      const users = cognitoData?.users || [];
      console.log('Loaded Cognito users for authorization:', users); // Debug log
      setCognitoUsers(users);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncUser = async (cognitoUser) => {
    setSyncing(prev => ({ ...prev, [cognitoUser.sub]: true }));

    try {
      // Use the authorization-specific sync endpoint
      await authorizationApi.syncSingleCognitoUser(cognitoUser.sub);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error syncing user:', err);
      alert(`Failed to sync user: ${err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [cognitoUser.sub]: false }));
    }
  };

  const handleSyncAllUsers = async () => {
    if (!window.confirm('Are you sure you want to sync all Cognito users to the authorization database?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await authorizationApi.syncCognitoUsers();
      await loadData();
      if (onRefresh) onRefresh();
      
      alert(`Sync completed!\n\nCreated: ${result.users_created}\nSkipped: ${result.users_skipped}\nErrors: ${result.errors}`);
    } catch (err) {
      console.error('Error syncing all users:', err);
      alert(`Failed to sync users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManageRoles = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const handleAssignRole = async (roleId) => {
    if (!selectedUser) return;

    // Errors handled automatically by apiClient
    await authorizationApi.assignRole(selectedUser.user_sub, roleId);
    await loadData();
    if (onRefresh) onRefresh();
    showToast('Role assigned successfully', 'success');
  };

  const handleRemoveRole = async (roleId) => {
    if (!selectedUser) return;

    if (!window.confirm('Are you sure you want to remove this role from the user?')) {
      return;
    }

    // Errors handled automatically by apiClient
    await authorizationApi.removeRole(selectedUser.user_sub, roleId);
    await loadData();
    if (onRefresh) onRefresh();
    showToast('Role removed successfully', 'success');
  };

  // Merge Cognito and DB users
  const mergedUsers = cognitoUsers
    .filter(cognitoUser => cognitoUser && cognitoUser.sub) // Filter out invalid users
    .map(cognitoUser => {
      const dbUser = dbUsers.find(u => u.user_sub === cognitoUser.sub);
      return {
        sub: cognitoUser.sub,
        email: cognitoUser.email || 'N/A',
        username: cognitoUser.username || cognitoUser.email || 'Unknown',
        status: cognitoUser.status || 'UNKNOWN',
        inDatabase: !!dbUser,
        dbUser: dbUser,
        roles: dbUser?.roles || []
      };
    });

  const filteredUsers = mergedUsers.filter(user =>
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.sub && user.sub.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const columns = [
    {
      header: 'Email',
      accessor: 'email',
      render: (user) => (
        <span className="email-col">
          {user.email || <span className="no-email">No email</span>}
        </span>
      )
    },
    {
      header: 'Status',
      width: '120px',
      render: (user) => (
        <span className={`status-text ${user.status === 'CONFIRMED' ? 'confirmed' : 'pending'}`}>
          {user.status}
        </span>
      )
    },
    {
      header: 'Sync Status',
      width: '120px',
      render: (user) => user.inDatabase
        ? <span className="status-text synced">Synced</span>
        : <span className="status-text not-synced">Not Synced</span>
    },
    {
      header: 'Roles',
      width: '100px',
      render: (user) => {
        if (user.inDatabase && user.roles && user.roles.length > 0) {
          return (
            <div className="roles-cell">
              {user.roles.slice(0, 3).map(roleId => {
                const role = roles.find(r => r.role_id === roleId);
                return (
                  <span key={roleId} className="role-text">
                    {role?.role_name || roleId}
                  </span>
                );
              })}
              {user.roles.length > 3 && (
                <span className="role-text">+{user.roles.length - 3}</span>
              )}
            </div>
          );
        }
        return <span className="no-roles">{user.inDatabase ? 'No roles' : '—'}</span>;
      }
    },
    {
      header: 'Actions',
      width: '160px',
      render: (user) => !user.inDatabase ? (
        <Button
          variant="primary"
          size="sm"
          icon="fas fa-sync"
          isLoading={syncing[user.sub]}
          onClick={() => handleSyncUser(user)}
        >
          Sync
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          icon="fas fa-user-tag"
          onClick={() => handleManageRoles(user.dbUser)}
        >
          Manage
        </Button>
      )
    }
  ];

  if (error) {
    return (
      <div className="users-tab">
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h4>Error Loading Users</h4>
          <p>{error}</p>
          <Button variant="primary" icon="fas fa-redo-alt" onClick={loadData}>
            Retry Connection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="users-tab">
      <div className="users-actions">
        <div className="search-wrapper">
          <SearchBar
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="users-stats">
          {dbUsers.length} / {cognitoUsers.length} synced
        </div>
        <Button variant="primary" icon="fas fa-sync-alt" onClick={handleSyncAllUsers} disabled={loading}>
          Sync All
        </Button>
        <Button variant="secondary" icon="fas fa-redo" onClick={loadData} disabled={loading} />
      </div>

      <Table
        columns={columns}
        data={filteredUsers}
        isLoading={loading}
        emptyMessage={searchTerm ? 'No users match your search criteria.' : 'No users found in Cognito.'}
        emptyIcon="fas fa-users"
      />

      {showRoleModal && selectedUser && (
        <RoleAssignmentModal
          user={selectedUser}
          roles={roles}
          onAssign={handleAssignRole}
          onRemove={handleRemoveRole}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
}
